import { useState } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { AGGREGATE_NAMES } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuRefreshCw } from 'react-icons/lu';

import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import useLead from '~/features/leadDetails/hooks/useLead';
import { useOffersSynchronizationMutation } from '~/features/leadDetails/hooks/useOffersSynchronizationMutation';
import { getMatchingEuropacePlatformPartnerId } from '~/features/leadDetails/utils/getMatchingEuropacePartnerId';
import useUserInfo from '~/hooks/useUserInfo';
import { cn } from '~/utils/cnTailwind';

type Props = {
    leadId: string;
};

export const SynchronizeOffersButton: React.FC<Props> = ({ leadId }) => {
    const { isPending, mutate: offersSynchronization } = useOffersSynchronizationMutation(leadId);
    const [openTooltip, setOpenTooltip] = useState(false);
    const { t } = useTranslation();
    const { data: lead } = useLead(leadId);
    const user = useUserInfo();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { partnerId: matchingPartnerId } = getMatchingEuropacePlatformPartnerId(
        lead?.europace?.platform ?? null,
        user,
    );
    const hasEuropaceProcessId = lead?.europace?.id != null;

    const disabled =
        !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].requestEuropaceOffersSynchronization ||
        !hasEuropaceProcessId ||
        !matchingPartnerId;

    const onOpenTooltip = (triggeredOpen: boolean) => {
        setOpenTooltip(triggeredOpen && (!hasEuropaceProcessId || !matchingPartnerId));
    };

    return (
        <Tooltip open={openTooltip} onOpenChange={onOpenTooltip}>
            <TooltipTrigger>
                <Button
                    variant={ButtonVariant.SECONDARY}
                    disabled={disabled}
                    iconLeft={<LuRefreshCw className={cn('h-4 w-4', isPending && 'animate-spin')} />}
                    onClick={() => offersSynchronization()}
                >
                    {t('OFFER_MANAGEMENT.OFFERS_SYNCHRONIZATION.BUTTON_TITLE')}
                </Button>
            </TooltipTrigger>
            <TooltipContent>
                {!hasEuropaceProcessId
                    ? t('OFFER_MANAGEMENT.OFFERS_SYNCHRONIZATION.TOOLTIP_CONTENT.NOT_IMPORTED')
                    : t('OFFER_MANAGEMENT.OFFERS_SYNCHRONIZATION.TOOLTIP_CONTENT.MISSING_EUROPACE_PARTNER_ID')}
            </TooltipContent>
        </Tooltip>
    );
};
